import { ProductLevel } from "./enums";
import type {
  GetCountriesQuery,
  GetLocationsQuery,
  ProductMetadataFragment,
} from "../graphql/__generated__/graphql";

/**
 * Shape of a product as returned by the GraphQL ProductMetadata fragment.
 * Derived from the generated `ProductMetadataFragment` type with `productLevel`
 * narrowed to our domain `ProductLevel` enum.
 */
export type ProductMetadatum = Omit<
  ProductMetadataFragment,
  "productLevel" | "parent" | "topParent" | "__typename"
> & {
  productLevel: ProductLevel;
  parent: { productId: string; productLevel: ProductLevel } | null;
  topParent: { productId: string; productLevel: ProductLevel } | null;
};

/**
 * Products metadata grouped by level, as returned by useProductsMetadata.
 */
export interface ProductMetadataByLevel {
  section: ProductMetadatum[];
  twoDigit: ProductMetadatum[];
  fourDigit: ProductMetadatum[];
  sixDigit: ProductMetadatum[];
}

/**
 * Deflator datum as returned by the GetDeflators query.
 */
export interface DeflatorDatum {
  year: number | null;
  deflator: number | null;
}

/** Country row from `GetCountries` / `GetLocations` (GraphQL `LocationCountry`). */
export type CountryDatum = NonNullable<
  NonNullable<GetCountriesQuery["countries"]>[number]
>;

/** Region or subregion row from `GetLocations` (GraphQL `LocationGroup`). */
export type LocationGroupDatum = NonNullable<
  NonNullable<GetLocationsQuery["regions"]>[number]
>;

/**
 * GraphQL returns member ids as strings; apps often compare against numeric ids from URLs.
 * Use this instead of `members.includes(id)` so `null` entries and type mismatch are handled.
 */
export function locationMembersInclude(
  members: readonly (string | number | null | undefined)[] | null | undefined,
  id: string | number | null | undefined,
): boolean {
  if (id == null || !members?.length) return false;
  const idStr = String(id);
  return members.some((m) => m != null && String(m) === idStr);
}

/**
 * Loose structural type for mixed location metadata (shared with lookups, titles, etc.).
 * Omits `__typename` before `Partial<>` so `LocationCountry` and `LocationGroup` rows
 * from GraphQL are assignable without conflicting literal discriminants.
 */
export type LocationMetadatum = Partial<
  Omit<CountryDatum, "__typename">
> &
  Partial<Omit<LocationGroupDatum, "__typename">>;

/**
 * Narrow a `LocationMetadatum` to a country row (has `countryId`).
 *
 * Only checks the `countryId` discriminant. This is safe because
 * `LocationMetadatum` values originate from GraphQL queries that always
 * return complete objects — the presence of `countryId` is sufficient
 * to guarantee all `CountryDatum` fields are populated.
 */
export function isCountryDatum(
  loc: LocationMetadatum,
): loc is Omit<CountryDatum, "__typename"> {
  return "countryId" in loc && loc.countryId != null;
}

/**
 * Narrow a `LocationMetadatum` to a group row (has `groupId`).
 *
 * Only checks the `groupId` discriminant. This is safe because
 * `LocationMetadatum` values originate from GraphQL queries that always
 * return complete objects — the presence of `groupId` is sufficient
 * to guarantee all `LocationGroupDatum` fields are populated.
 */
export function isLocationGroupDatum(
  loc: LocationMetadatum,
): loc is Omit<LocationGroupDatum, "__typename"> {
  return "groupId" in loc && loc.groupId != null;
}

/** Region/subregion metadata shape used in treemap and overtime location transforms. */
export interface RegionMetadata {
  groupId: string;
  groupName: string;
  members: (string | number | null)[];
  parentId?: string | null;
}

/** Country metadata shape used in treemap and overtime location transforms. */
export interface CountryMetadata {
  countryId: number;
  nameEn: string;
  iso3Code?: string | null;
  nameShortEn?: string | null;
}

/**
 * Country-year or group-year datum.
 */
export interface LocationYearDatum {
  countryId?: number;
  groupId?: number;
  year: number;
  population: number | null;
}

/** Location metadata payload shape (what useFetchMetadata returns for locations) */
export interface LocationMetadataPayload {
  regions: LocationGroupDatum[];
  subregions: LocationGroupDatum[];
  countries: CountryDatum[];
}

/** Product complexity payload */
export type ProductComplexityPayload = ProductYearDatum[];

/**
 * Product-year datum from the GetProductYearData query.
 */
export interface ProductYearDatum {
  productId: string;
  year: number;
  pci: number | null;
  exportValue: number | null;
  importValue: number | null;
}

/**
 * Constraint for data that can be indexed by year.
 */
export interface YearIndexable {
  year?: number | null;
}

/**
 * Input type for the product hierarchy stratify operation.
 * The root node has productId "root" and parent null; other entries are ProductMetadatum.
 */
export type StratifyProductInput =
  | { productId: string; parent: null }
  | ProductMetadatum;
